/**
 * Marketplace export sheet builder (005 US2, FR-004).
 *
 * One row per design: SKU, Phone Model, Case Style, Design Name, View 1..5 URL,
 * Print File URL. Ungenerated views → empty cells (NOT broken refs). CSV is
 * always available; XLSX is optional (zip-based, dependency-free writer).
 */
import { createZip } from "@genz/platform";
import type { ViewName } from "@genz/platform";
import { ALL_VIEWS } from "../templates/views.js";
import { buildRef, buildSku, toHostedUrl, type RefScheme } from "./refs.js";

/**
 * Ordered slots for the View columns — the FULL canonical view vocabulary.
 *
 * This was a hand-written 5-view list (`back/front/left/right/top`) which both dropped 2 of
 * the 7 real views (`floating`, `camera`, `isometric`) and included `top`, which is not a
 * view the app renders at all. Anything generated for a missing view simply had no cell.
 */
const VIEW_SLOTS: readonly ViewName[] = ALL_VIEWS;

export const SHEET_COLUMNS = [
  "SKU",
  "Phone Model",
  "Case Style",
  "Design Name",
  ...VIEW_SLOTS.map((_, i) => `View ${i + 1} URL`),
  "Print File URL",
] as const;

export interface SheetRowInput {
  customer: string;
  product: string;
  phoneModel: string; // human-readable
  caseStyle: string; // human-readable
  phoneSlug: string;
  caseStyleSlug: string;
  designName: string;
  designSlug: string;
  ext: "jpg" | "png" | "webp";
  /** Which views were actually generated (others → empty cell). */
  generatedViews: ViewName[];
  /** Whether a print file exists for this design. */
  printFile: boolean;
  /** Phase 3 host prefix; when set, cells carry hosted CDN URLs. */
  host?: string;
  /**
   * Path scheme the FILES were actually written with. MUST match the writer or every cell
   * points at a path that doesn't exist — the sheet hard-coded the `legacy` scheme while
   * `runBatch` has written the `mockups` scheme for a long time, so every URL in every
   * exported sheet was wrong. Defaults to `mockups` (what the render path emits).
   */
  scheme?: RefScheme;
  /** Mockups-scheme parts — same values `runBatch` passed to `buildRef` (see RefParts). */
  templateName?: string;
  productType?: string;
  brand?: string;
  model?: string;
  category?: string;
  /**
   * AUTHORITATIVE per-view refs, taken straight from the rendered `OutputRecord.localRef`
   * (view → ref). When supplied these win over anything rebuilt from parts, which removes
   * the whole "sheet drifted from the writer" failure class: the cell is literally the path
   * the file was written to. Callers without records fall back to the parts above.
   */
  viewRefs?: Record<string, string>;
}

export interface SheetRow {
  cells: string[];
}

export function buildSheetRow(input: SheetRowInput): SheetRow {
  const sku = buildSku({
    phoneSlug: input.phoneSlug,
    caseStyleSlug: input.caseStyleSlug,
    designSlug: input.designSlug,
  });
  // Every cell is built from the SAME parts the writer used, so the sheet's URLs resolve
  // 1:1 onto the files on disk / the CDN.
  const refFor = (view: ViewName | string) => {
    const actual = input.viewRefs?.[view];
    if (actual) return input.host ? toHostedUrl(actual, input.host) : actual;
    return buildRef({
      scheme: input.scheme ?? "mockups",
      customer: input.customer,
      product: input.product,
      templateName: input.templateName,
      phoneSlug: input.phoneSlug,
      caseStyleSlug: input.caseStyleSlug,
      designSlug: input.designSlug,
      designName: input.designName,
      category: input.category,
      productType: input.productType,
      brand: input.brand,
      model: input.model,
      view,
      ext: input.ext,
      host: input.host,
    });
  };
  const viewCells = VIEW_SLOTS.map((view) => (input.generatedViews.includes(view) ? refFor(view) : ""));
  const printCell = input.printFile ? refFor("print") : "";
  return {
    cells: [sku, input.phoneModel, input.caseStyle, input.designName, ...viewCells, printCell],
  };
}

export function buildSheet(rows: SheetRowInput[]): { columns: readonly string[]; rows: SheetRow[] } {
  return { columns: SHEET_COLUMNS, rows: rows.map(buildSheetRow) };
}

// --- CSV (always) ----------------------------------------------------------

/**
 * Does this value lead with something a spreadsheet reads as "this cell is a FORMULA"?
 *
 * `=`, `+`, `-` and `@` are the lead characters. Any run of WHITESPACE in front of one is
 * skipped before the decision is made, so `\t=1+1`, `\n=1+1` and ` =1+1` are all evaluated
 * exactly as `=1+1` is — the set started as TAB and CR only, which let the newline and the
 * plain space straight through (closer 5).
 */
const FORMULA_LEAD = /^\s*[=+\-@]/;

/**
 * Make a cell inert (review-2 item 7).
 *
 * Every mainstream spreadsheet evaluates a leading `=`, `+`, `-` or `@`, so a value that came
 * from DATA becomes code the moment the operator opens the export — `=HYPERLINK(…)`
 * phishing, `=cmd|…` on Windows, or a silent arithmetic result where a product name should
 * be. The columns this reaches are no longer a closed vocabulary: F-7 put the operator's own
 * Phone Model / Case Style names in them, beside a Design Name that has always been a
 * filename. The standard mitigation is a leading apostrophe, which spreadsheets consume and
 * treat as "this is text".
 *
 * The XLSX writer needs no twin: it emits `t="inlineStr"` cells, which are never parsed as
 * formulas.
 */
function csvNeutralize(value: string): string {
  return FORMULA_LEAD.test(value) ? `'${value}` : value;
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/** Neutralize THEN quote, so the apostrophe lands inside the quotes and is part of the cell. */
function csvCell(value: string): string {
  return csvEscape(csvNeutralize(value));
}

export function toCsv(rows: SheetRowInput[]): string {
  const sheet = buildSheet(rows);
  // The header is a fixed vocabulary this module owns — escaped, never neutralized, so a
  // column can never grow a stray apostrophe that an importer would key on.
  const lines = [sheet.columns.map(csvEscape).join(",")];
  for (const r of sheet.rows) lines.push(r.cells.map(csvCell).join(","));
  return lines.join("\r\n");
}

export function toCsvBytes(rows: SheetRowInput[]): Uint8Array {
  // Prepend UTF-8 BOM so spreadsheets open it correctly.
  const bom = Uint8Array.from([0xef, 0xbb, 0xbf]);
  const body = new TextEncoder().encode(toCsv(rows));
  return Uint8Array.from([...bom, ...body]);
}

// --- XLSX (optional, dependency-free minimal writer) -----------------------

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Minimal single-sheet XLSX (Office Open XML) using inline strings. */
export function toXlsxBytes(rows: SheetRowInput[]): Uint8Array {
  const sheet = buildSheet(rows);
  const allRows = [[...sheet.columns], ...sheet.rows.map((r) => r.cells)];
  const sheetRowsXml = allRows
    .map((cells, ri) => {
      const cs = cells
        .map((c, ci) => {
          const ref = `${colLetter(ci)}${ri + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(c)}</t></is></c>`;
        })
        .join("");
      return `<row r="${ri + 1}">${cs}</row>`;
    })
    .join("");

  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRowsXml}</sheetData></worksheet>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`;
  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Export" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`;

  const enc = (s: string) => new TextEncoder().encode(s);
  return createZip([
    { name: "[Content_Types].xml", bytes: enc(contentTypes) },
    { name: "_rels/.rels", bytes: enc(rootRels) },
    { name: "xl/workbook.xml", bytes: enc(workbook) },
    { name: "xl/_rels/workbook.xml.rels", bytes: enc(workbookRels) },
    { name: "xl/worksheets/sheet1.xml", bytes: enc(sheetXml) },
  ]);
}

function colLetter(index: number): string {
  let s = "";
  let n = index;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}
